import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';

import { ProjectSetup, UserSession } from '@shared/models';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { DataRefreshService, SignOutService } from '.';
import { PersistenceService } from './persistence';
import { ConfirmDialogComponent } from '@shared/components';
import { Constants, getStaticMessage } from '@shared/utils';
import { RefreshType, StaticMessage } from '@shared/enums';

@UntilDestroy()
@Injectable()
export class AfterOfflineSyncService {
  private isAppOnline$ = this.appFacade.query.isAppOnline$;
  private selectedSubprojectId$ =
    this.userDetailsFacade.query.selectedSubprojectId$;
  private projectSetup$ = this.userDetailsFacade.query.projectSetup$;
  private userDetails$ = this.userDetailsFacade.query.userDetails$;

  constructor(
    private appFacade: AppFacade,
    private userDetailsFacade: UserDetailsFacade,
    private persistenceService: PersistenceService,
    private dataRefreshService: DataRefreshService,
    private signOutService: SignOutService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  processAfterOfflineWithoutSync(): void {
    this.dataRefreshService.setRefreshNeeded(); // needed to refresh the DPR cards

    this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        take(1),
        mergeMap((isAppOnline) => {
          // triggered after coming back online without doing an offline sync
          return isAppOnline
            ? this.checkForSetupAndUserSessionChanges(true)
            : of(null);
        })
      )
      .subscribe();
  }

  processAfterOfflineSync(): void {
    this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        take(1),
        mergeMap((isAppOnline) => {
          // triggered after completing the offline sync when coming back online
          return isAppOnline
            ? this.checkForSetupAndUserSessionChanges(false)
            : of(null);
        }),
        tap(() => {
          // trigger the page reload only if no other reload was triggered
          this.dataRefreshService.setRefreshNeeded();
        })
      )
      .subscribe();
  }

  userSessionChanged(data: UserSession): Observable<boolean> {
    return this.userDetails$.pipe(
      take(1),
      map(
        (userDetails) =>
          !this.equals(data.subprojects, userDetails.subprojects) ||
          !this.equals(data.clientsList, userDetails.clientsList) ||
          !this.equals(data.projects, userDetails.projects) ||
          data.settingsRight !== userDetails.settingsRight ||
          data.userAdminRight !== userDetails.userAdminRight
      )
    );
  }

  //#region Private methods

  private checkForSetupAndUserSessionChanges(
    triggerReload: boolean
  ): Observable<[ProjectSetup, UserSession]> {
    // reload the user session and project setup
    // to cover the case when the project setup, settings or user rights changed while the user was offline

    return this.selectedSubprojectId$.pipe(
      take(1),
      mergeMap((selectedSubprojectId) => {
        const reloadProjectSetup$ = this.persistenceService
          .reloadProjectSetup(selectedSubprojectId, false)
          .pipe(
            tap((projectSetup) => {
              this.checkForProjectSetupChanges(
                projectSetup,
                $localize`:@@shared.afterOfflineSyncService.titleProjectSetupChanged:Project setup changed.`,
                $localize`:@@shared.afterOfflineSyncService.txtProjectSetupChanged:An admin user has updated the project setup. The page will be reloaded.`,
                triggerReload
              );
            })
          );

        const reloadUserSession$ = this.persistenceService
          .reloadUserSession(false)
          .pipe(
            tap((userDetails) => {
              this.checkForUserSessionChanges(
                userDetails,
                $localize`:@@shared.afterOfflineSyncService.titleUserRightsOrProjectSettingsChanged:User rights or project settings changed.`,
                $localize`:@@shared.afterOfflineSyncService.txtUserRightsOrProjectSettingsChanged:An admin user has updated your user rights or the project settings. The page will be reloaded.`,
                triggerReload
              );
            }),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 400) {
                const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                  ...Constants.DialogOptions,
                  data: {
                    title: $localize`:@@shared.afterOfflineSyncService.titleAccountDeleted:Account deleted.`,
                    message: $localize`:@@shared.afterOfflineSyncService.txtAccountDeleted:Your account has been deleted by an admin user.</br></br> You will be logged out of the system.`,
                    buttonLabel: getStaticMessage(StaticMessage.OkButton),
                    hideCancelButton: true,
                  },
                });

                dialogRef
                  .afterClosed()
                  .pipe(
                    untilDestroyed(this),
                    tap(() => {
                      this.signOutService.signOut();
                    })
                  )
                  .subscribe();
              }

              return of(null);
            })
          );

        return forkJoin([reloadProjectSetup$, reloadUserSession$]);
      })
    );
  }

  private checkForProjectSetupChanges(
    projectSetup: ProjectSetup,
    title: string,
    message: string,
    triggerReload: boolean
  ): void {
    this.projectSetupChanged(projectSetup)
      .pipe(
        untilDestroyed(this),
        filter((projectSetupChanged) => projectSetupChanged),
        tap(() => {
          this.userDetailsFacade.setProjectSetup(projectSetup);

          if (triggerReload && this.pageReloadNeededForProjectSetup()) {
            this.displayDialogAndReload(
              title,
              message,
              RefreshType.ProjectSetupChange
            );
          }
        })
      )
      .subscribe();
  }

  private checkForUserSessionChanges(
    userDetails: UserSession,
    title: string,
    message: string,
    triggerReload: boolean
  ): void {
    this.userSessionChanged(userDetails)
      .pipe(
        untilDestroyed(this),
        filter((userSessionChanged) => userSessionChanged),
        tap(() => {
          this.userDetailsFacade.updateUserSession(userDetails);

          if (triggerReload) {
            this.displayDialogAndReload(
              title,
              message,
              RefreshType.UserSessionChange
            );
          }
        })
      )
      .subscribe();
  }

  private displayDialogAndReload(
    title: string,
    message: string,
    refreshType: RefreshType
  ): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title,
        message,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.dataRefreshService.setRefreshNeeded(refreshType);
        })
      )
      .subscribe();
  }

  private projectSetupChanged(data: ProjectSetup): Observable<boolean> {
    return this.projectSetup$.pipe(
      take(1),
      map((projectSetup) => !this.equals(data, projectSetup))
    );
  }

  private equals(data1: any, data2: any): boolean {
    const sanitizeDataFunction = (key: string, val: any) => {
      return key !== 'canBeDeleted' && key !== 'showTab' && key !== '$$hashKey'
        ? val
        : undefined;
    };

    // exclude the canBeDeleted project setup property and $$hashKey, because it is not relevant for the comparison
    const data1String = JSON.stringify(data1, sanitizeDataFunction);
    const data2String = JSON.stringify(data2, sanitizeDataFunction);
    return data1String === data2String;
  }

  private pageReloadNeededForProjectSetup(): boolean {
    return (
      this.router.url.includes('dpr-details') ||
      this.router.url.includes('dprs') ||
      this.router.url.includes('project-setup')
    );
  }

  //#endregion
}
